import { api } from '../../lib/api'
import { readDataTable } from '../../lib/dataTable'

/**
 * System log endpoints.
 *
 *   listActivities    -> POST /SystemState/GetActivities        REAL
 *   listBatches       -> POST Messages/FilterBatches            still mock (NotificationsDetails)
 *   listBatchMessages -> POST Messages/FilterBatchMessages      still mock
 *   deleteBatch       -> POST NotificationsDetails/Delete       still mock
 *
 * Only the activity log is integrated. The three below belong to `NotificationsDetails`, the next
 * slice in the phase, and are left exactly as they were.
 */

const ACTIVITIES = '/SystemState/GetActivities'

/**
 * THE ROW HAS NO FIELD NAMES.
 *
 * `ActivityDto` declares its properties as `_1 _2 _3 _4` — the DataTables positional-array
 * convention leaked into a C# DTO — so the wire looks like
 *
 * ```json
 * {"_1": 0, "_2": "<a href=\"…/UsersDetails?UserId=…\">rawamadmin</a>", "_3": "حذف حساب", "_4": "09-08-2026 11:30 AM"}
 * ```
 *
 * They are, in order: row number, username-as-an-anchor, activity name, formatted date. Named
 * here so nothing downstream ever sees an underscore. API_FINDINGS.md §65.
 */
const ROW_NUMBER = '_1'
const USER_ANCHOR = '_2'
const ACTIVITY = '_3'
const WHEN = '_4'

/**
 * The username out of the anchor the handler builds.
 *
 * `GenerateUserLink` wraps the name in `<a href="{DomainUrl}/UsersDetails?UserId={guid}">`, and
 * returns the bare name when `DomainUrl` is empty — so both shapes arrive. The markup is dropped
 * either way and the screen builds its own link; the href is useless anyway, since `/UsersDetails`
 * is not a route in this product (the real one is `/UserDetailsAdmin`).
 */
function textFromAnchor(value) {
  if (!value) return ''
  const match = /<a[^>]*>([\s\S]*?)<\/a>/i.exec(value)
  return (match ? match[1] : value).replace(/<[^>]*>/g, '').trim()
}

/** The guid inside that anchor — the only place a row's user id appears. */
function idFromAnchor(value) {
  const match = /UserId=([0-9a-f-]{36})/i.exec(String(value ?? ''))
  return match ? match[1] : null
}

/**
 * A sort/compare key for a date the API already formatted as `dd-MM-yyyy hh:mm tt`.
 *
 * Compared as text it would order by day of month (§64). Nothing here sorts on it — the API's
 * order is fixed — but the summary needs to know which row is the most recent, and that must not
 * depend on string luck.
 */
function dateKey(formatted) {
  const parts = /^(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2}) (AM|PM)$/i.exec(String(formatted ?? '').trim())
  if (!parts) return 0
  const [, day, month, year, hour, minute, meridiem] = parts
  const hour24 = (Number(hour) % 12) + (meridiem.toUpperCase() === 'PM' ? 12 : 0)
  return Date.UTC(Number(year), Number(month) - 1, Number(day), hour24, Number(minute))
}

/**
 * The activity log — 5,682 rows here and one more on every login, so always server-paged.
 *
 * Three things this endpoint does that it does not announce:
 *
 * - **`length` is mandatory.** `DataTablesQueryParams` clamps it to 0–100 and the handler reads a
 *   non-nullable int, so a missing length is `Take(0)` — an empty list, HTTP 200. It is also a
 *   HARD CEILING of 100: there is no way to ask for the whole table.
 * - **The order is fixed.** `OrderByDescending(ActivityDate)` is applied unconditionally and the
 *   DataTables `order[…]` parameters are never read, so a sort request is silently ignored. The
 *   screen therefore offers no sortable column rather than one that does nothing.
 * - **`_1` is not a row number.** `queryParams?.Start ?? 0 + index + 1` binds as
 *   `Start ?? (0 + index + 1)`, and `Start` is never null — so every row on page 1 says 0 and
 *   every row on page 2 says 25. It is ignored here and the table numbers its own rows.
 */
export async function listActivities({ page = 1, pageSize = 25, query = '' } = {}, signal) {
  const params = new URLSearchParams({
    draw: '1',
    start: String((page - 1) * pageSize),
    length: String(Math.min(pageSize, 100)),
    'search[value]': query ?? '',
  })

  const dto = await api.post(ACTIVITIES, params, { signal })
  const { rows, total, totalUnfiltered } = readDataTable(dto)

  return {
    rows: rows.map((row, index) => ({
      /* The DTO carries no id — the handler selects one and drops it — so identity is the row's
         position in the page it came from. Stable for as long as the page is on screen, which is
         all `getRowId` needs. */
      id: `${(page - 1) * pageSize + index}`,
      username: textFromAnchor(row[USER_ANCHOR]),
      userId: idFromAnchor(row[USER_ANCHOR]),
      activity: row[ACTIVITY] ?? '',
      // Pre-formatted `dd-MM-yyyy hh:mm tt`; displayed as received, never re-parsed (W6).
      activityDate: row[WHEN] ?? '',
      when: dateKey(row[WHEN]),
      // Present, and deliberately unused — see the note above.
      serverRowNumber: row[ROW_NUMBER],
    })),
    /* `total` is what the pager divides — the count the SEARCH matched. `totalUnfiltered` is
       every activity ever recorded, which is what the summary counts. */
    total,
    totalUnfiltered,
  }
}

/**
 * THE NOTIFICATION HISTORY OF ONE RECORD — an invitation, a group email.
 *
 * `GET /api/admin/notifications-details/batches?objectId=&templateTypes=` — a JSON sibling
 * of the `NotificationsDetails` page route, guarded for `UsersManagers` because that is who
 * opens the modal that reads it.
 *
 * The server filters by object and type, so nothing is narrowed again here. Each batch
 * carries its messages: the batch's own state says the RUN finished, and only the message
 * says whether the address was actually reached.
 */
export async function listObjectBatches({ objectId, templateTypes = [], signal } = {}) {
  if (!objectId) return []

  const params = new URLSearchParams({ objectId: String(objectId) })
  if (templateTypes.length > 0) params.set('templateTypes', templateTypes.join(','))

  const dto = (await api.get(`/api/admin/notifications-details/batches?${params}`, { signal })) ?? []

  return dto.map((batch) => ({
    id: batch.Id,
    state: batch.State ?? '',
    templateType: batch.TemplateType,
    objectId: batch.ObjectId ?? '',
    scheduledStartDate: batch.ScheduledStartDate ?? '',
    startDate: batch.StartDate ?? '',
    endDate: batch.EndDate ?? '',
    recipients: batch.Recipients ?? '',
    messages: (batch.Messages ?? []).map((message) => ({
      id: message.Id,
      deliveryStatus: message.DeliveryStatus ?? '',
      tries: message.Tries ?? 0,
      lastTry: message.LastTry ?? '',
      channel: message.Channel ?? '',
      receiver: message.Receiver ?? '',
      error: message.Error ?? '',
    })),
  }))
}

/* Scaffolding for the three mocks below only — `listActivities` above is real and does not use
   it. Both go when NotificationsDetails is integrated. */
const NETWORK_DELAY = 220
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

export async function listBatches({ type = '', status = '' } = {}) {
  await delay(NETWORK_DELAY)
  const { notificationBatches } = await import('../../data/notificationBatches')
  const { notificationTemplates } = await import('../../data/systemLogs')

  const byType = new Map(notificationTemplates.map((template) => [template.type, template]))
  const rows = notificationBatches
    .filter((batch) => (type ? String(batch.templateType) === String(type) : true))
    .filter((batch) => (status ? batch.state === status : true))
    .map((batch) => ({
      ...batch,
      /* The view resolves MessageTemplateType against the template list and falls back
         to the raw number when nothing matches. */
      typeName: byType.get(batch.templateType)?.name ?? String(batch.templateType),
    }))

  return { rows, templates: notificationTemplates }
}

export async function listBatchMessages(batchId) {
  await delay(NETWORK_DELAY)
  const { batchMessages } = await import('../../data/systemLogs')
  return { rows: batchMessages.filter((message) => message.batchId === batchId) }
}

export async function deleteBatch(batchId) {
  await delay(NETWORK_DELAY)
  return { ok: true, batchId }
}
